// Which addresses this platform is allowed to email.
//
// `EMAIL_ALLOWED_RECIPIENT_DOMAINS` is a list of domains a project may write
// to. Staging is set to `80days.co.jp`, so an invitation to a colleague
// genuinely arrives — the only way to see what the email actually looks like —
// while one typed at a real partner address is refused before it leaves the
// server, and handed back to the admin as a link instead.
//
// Unset means unrestricted, which is what production wants and what a project
// that forgets to configure it gets. A restriction that silently switched
// itself on in production would be a worse failure than an unrestricted
// staging.
//
// This replaced `EMAIL_SANDBOX_MODE`, which asked SendGrid to validate every
// message and deliver none of it. All or nothing was the problem: staging could
// only send nothing, so the emails themselves went unread until production sent
// them for real. A stale `EMAIL_SANDBOX_MODE=true` left in a local .env is
// inert — nothing reads it any more.
//
// Shared by the Cloud Functions (which enforce it) and the admin UI (which
// warns about it before anyone clicks Send).

/**
 * The recipient domains staging is configured to email.
 *
 * Kept here because the admin UI has to be able to say so, and the UI has no
 * way to read a Cloud Functions param. It must match
 * `EMAIL_ALLOWED_RECIPIENT_DOMAINS` in `functions/.env.eighty-days-exp-staging`
 * — the server is the one that enforces it, so if the two ever disagree, this
 * one is merely wrong about what will happen.
 */
export const stagingAllowedRecipientDomains = ['80days.co.jp'];

/**
 * Reads the `EMAIL_ALLOWED_RECIPIENT_DOMAINS` param — a comma-separated list of
 * domains, tolerant of spaces and a leading `@` because it is typed by hand
 * into a `.env` file. An empty value yields an empty list, meaning no
 * restriction at all.
 */
export function parseAllowedRecipientDomains(raw: string): string[] {
  return raw
    .split(',')
    .map((domain) => domain.trim().toLowerCase().replace(/^@/, ''))
    .filter(Boolean);
}

/**
 * Whether an address may be emailed under the given allowlist. An empty
 * allowlist allows everything.
 *
 * Matches the domain exactly: `80days.co.jp` does not admit
 * `mail.80days.co.jp`, and — the reason this is worth stating — nor does it
 * admit `80days.co.jp.example.com`.
 */
export function isRecipientDomainAllowed(email: string, allowedDomains: string[]): boolean {
  if (allowedDomains.length === 0) return true;
  const domain = email.trim().toLowerCase().split('@')[1] ?? '';
  return allowedDomains.includes(domain);
}

/** How the restriction is described wherever it has to be explained. */
export function allowedRecipientDomainsLabel(allowedDomains: string[]): string {
  const domains = allowedDomains.map((domain) => `@${domain}`);
  if (domains.length <= 1) return domains[0] ?? '';
  return `${domains.slice(0, -1).join(', ')} and ${domains[domains.length - 1]}`;
}
